import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleInitGame, handleGetBalanceStrategy, handleGetBalance } from "../../service";
import { loadAssets } from "./assetsSlice";
import { loadSounds } from "./soundsSlice";
import { rgsPlay, rgsBuyFeature } from "./spinSlice";
import StorageManager from "../../utils/StorageManager";
import { DEV_SIMULATION_ENABLED, DEV_SIMULATION_ROUND } from "../../devSimulation";

// Async thunk for initializing the game and fetching initial data
export const initializeGame = createAsyncThunk(
  "gameSettings/initializeGame",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // Clear legacy prize/winners data (unused feature)
      StorageManager.clearPrizeWinnersData();

      // API call FIRST - validate session before downloading heavy assets
      // If this fails (expired session, server down, invalid token),
      // the user sees an error immediately without wasting bandwidth
      const result = await handleInitGame();

      // Session is valid — now load assets and sounds
      await dispatch(loadAssets());
      await dispatch(loadSounds());

      // Data is loaded - feature slices will react to universal state
      return { result };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Heartbeat sync - calls init API to sync balance
 * Does NOT reload assets/sounds, only updates balance
 * CRITICAL: Only extracts balance to avoid unnecessary component re-renders
 * Note: Backend treats this as session keep-alive, but frontend only cares about balance
 */
export const heartbeatSync = createAsyncThunk(
  "gameSettings/heartbeatSync",
  async (_, { rejectWithValue }) => {
    try {
      const result = await handleInitGame();
      // Return only balance to avoid triggering unnecessary updates
      return { balance: result.balance };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sync balance with server - calls provider's getBalance() endpoint
 * Lightweight: only fetches and updates balance, nothing else
 * Use after free spin outro, win animations, or anywhere balance needs server sync
 */
export const syncBalance = createAsyncThunk(
  "gameSettings/syncBalance",
  async (_, { rejectWithValue }) => {
    try {
      const { balance } = await handleGetBalance();
      return { balance };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Refresh game data - lightweight init without asset reloading
 * Used for error 205 (free rounds) to fetch updated game state
 * Does NOT reset spinSlice state, preserving spinComplete and autoplay
 * @param {Object} options - Options object
 * @param {boolean} options.useApiData - If true, uses fresh API data instead of stored data (for error recovery)
 * @param {boolean} options.ignoreFreeRoundsStorage - If true, ignores free rounds localStorage (for new batch activation)
 */
export const refreshGameData = createAsyncThunk(
  "gameSettings/refreshGameData",
  async (options = {}, { rejectWithValue }) => {
    try {
      const { useApiData = false, ignoreFreeRoundsStorage = false } =
        typeof options === "boolean" ? { useApiData: options } : options;

      // API call only - RGS handles feature detection
      const result = await handleInitGame(useApiData, ignoreFreeRoundsStorage);

      // Feature slices will react to universal state
      return { result };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

// Initial state for the game settings slice
const initialState = {
  token: "",
  balance: 0,
  displayedBalance: 0, // What the UI should show
  bet: 0,
  baseBet: 1, // Base bet for paytable calculations (default to 1 to avoid division by zero)
  bets: [],
  currency: "",
  currencyConfig: {
    subunits: 100, // Number of subunits per main currency unit (default: 100 for cents/dollars)
    exponent: 2, // Number of decimal places to display (default: 2 for "10.00")
    symbol: "", // Currency symbol (e.g., "$", "€", "FUN")
  },
  paytable: [],
  rtp: null, // RTP value from API init (e.g., 96.5)
  bonusBuyMultiplier: 60, // Bonus buy cost multiplier (60x bet)
  currentRound: {
    reels: [],
    wins: [],
    win: 0,
  },
  winningLines: [],
  status: "idle",
  error: null,
  errorCode: null, // Store error code from API (e.g., 301)
  errorDescription: null, // Store error description from API (e.g., "not_enough_money")
  // Universal state fields (provider-agnostic)
  gameMode: "base", // "base" | "feature" - universal game mode
  featureState: { type: null, phase: null, data: null }, // Universal feature state
  reloadState: { needsReload: false }, // Universal reload state

  // Legacy provider-specific fields (will be removed in Phase 11)
  availableActions: ["init", "spin"], // Track available actions from API (spin vs respin)
  flowState: "ready", // Track flow.state from API (ready, closed, freespins)
  features: null, // Track respin features: {respins_issued, respins_left, trigger_screen}
  lastCommand: null, // Track the command sent to API ("spin" or "respin")
  heartbeatError: {
    consecutiveFailures: 0,
    state: "none", // none, warning (3+ failures), critical (10+ failures)
  },
  freeRounds: null, // Casino-granted free rounds data from API
};

// Create a slice for managing game settings
const gameSettingsSlice = createSlice({
  name: "gameSettings",
  initialState,
  reducers: {
    incrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex < state.bets.length - 1) {
        state.bet = state.bets[currentIndex + 1];
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
      }
    },

    decrementBet(state) {
      const currentIndex = state.bets.indexOf(state.bet);
      if (currentIndex > 0) {
        state.bet = state.bets[currentIndex - 1];
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
      }
    },

    setBet(state, action) {
      const betValue = action.payload;
      if (state.bets.includes(betValue)) {
        state.bet = betValue;
        const isFeatureActive = state.gameMode === "feature";
        if (!isFeatureActive) {
          if (state.currentRound?.reels?.length) {
            // CRITICAL: Preserve existing round data, only update bet and balance
            // This prevents overwriting good round data with H&W outro round (coins/blanks)
            const existingBaseGame = StorageManager.loadBaseGameRound();
            const roundToSave = existingBaseGame?.round || state.currentRound;

            StorageManager.saveBaseGameRound({
              round: roundToSave,
              bet: state.bet,
              balance: state.balance,
            });
          }
        }
      }
    },

    setBaseBet(state, action) {
      state.baseBet = action.payload || 1;
    },

    setBalance(state, action) {
      state.balance = Math.max(0, action.payload);
      state.displayedBalance = Math.max(0, action.payload); // Also update displayedBalance so UI shows the change
    },

    setDisplayedBalance(state, action) {
      state.displayedBalance = Math.max(0, action.payload);
    },

    updateDisplayedBalance(state, action) {
      // BALANCE STRATEGY: This reducer syncs displayedBalance after win animations complete
      // Behavior depends on provider's balance strategy:
      // - If serverIncludesWin (Redstone): Just sync to balance (already has win)
      // - If !serverIncludesWin (PopiPlay): Add win to balance, then sync
      //
      // For now, just syncing to balance works for Redstone
      // PopiPlay adapter will handle win addition in its own flow

      if (action.payload !== undefined) {
        state.displayedBalance = Math.max(0, action.payload);
      } else {
        // Sync displayedBalance to current balance
        state.displayedBalance = Math.max(0, state.balance);
      }
    },

    setAvailableActions(state, action) {
      state.availableActions = action.payload;
    },

    setFlowState(state, action) {
      state.flowState = action.payload;
    },

    setLastCommand(state, action) {
      state.lastCommand = action.payload;
    },

    restoreSavedBet(state) {
      // Restore bet from base game round (with session validation)
      if (!StorageManager.isSessionValid()) return;
      const baseGame = StorageManager.loadBaseGameRound();
      const savedBet = baseGame?.bet;
      if (savedBet && state.bets.includes(savedBet)) {
        state.bet = savedBet;
      }
    },

    /**
     * Clear featureState when returning to base game
     * Dispatched from SceneTransitionOverlay when exiting H&W
     */
    clearFeatureStateToBase(state) {
      state.featureState = { type: null, phase: null, data: null };
      state.gameMode = "base";
    },

    /**
     * Set featureState to free rounds when exiting H&W back to free rounds
     * Next spin will use "spin" with bonus options
     */
    setFeatureStateFromFreeRounds(state, action) {
      const { betValue, betLevel, count, performed, totalWin } = action.payload;
      state.featureState = {
        type: "freeRounds",
        phase: "active",
        data: {
          betValue: betValue ?? 0,
          betLevel: betLevel ?? null,
          count: count ?? 0,
          performed: performed ?? 0,
          totalWin: totalWin ?? 0,
        },
      };
      state.gameMode = "feature";
    },

    /**
     * Update reel symbols when expanding wilds replace them
     * This ensures the Redux state reflects the wild replacements
     * so that next spin reads correct symbols
     */
    updateReelSymbols(state, action) {
      const { reelIndex, symbolReplacements } = action.payload;

      if (state.currentRound?.reels?.[reelIndex]) {
        // symbolReplacements is an array of { position, symbolId }
        symbolReplacements.forEach(({ position, symbolId }) => {
          state.currentRound.reels[reelIndex][position] = symbolId;
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeGame.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.isLoading = true;
      })
      .addCase(initializeGame.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Simple assignment - no logic!
        // RGS has already handled all feature detection and storage operations
        state.token = result.token;
        state.balance = Math.max(0, result.balance);
        state.displayedBalance = Math.max(0, result.balance);
        state.bet = result.bet;
        state.baseBet = result.baseBet || 1;
        state.bets = result.bets;
        state.currency = result.currency;
        state.paytable = result.paytable || [];
        state.rtp = result.rtp || window.__OPTIONS__?.math?.rtp?.main || null;
        state.winningLines = result.winningLines || [];

        // Store currency configuration
        if (result.currencyConfig) {
          state.currencyConfig = {
            subunits:
              result.currencyConfig.subunits !== undefined
                ? result.currencyConfig.subunits
                : 100,
            exponent:
              result.currencyConfig.exponent !== undefined
                ? result.currencyConfig.exponent
                : 2,
            symbol: result.currencyConfig.symbol || "",
          };
        }

        // Set bonus buy multiplier if provided
        if (result.bonusBuyMultiplier !== undefined) {
          state.bonusBuyMultiplier = result.bonusBuyMultiplier;
        }

        // Set current round if provided
        if (result.round) {
          state.currentRound = result.round;
        }

        // Universal state fields (provider-agnostic)
        state.gameMode = result.gameMode || "base";
        state.featureState = result.featureState || {
          type: null,
          phase: null,
          data: null,
        };
        state.reloadState = result.reloadState || { needsReload: false };

        // Provider-specific fields (for backward compatibility during migration)
        state.availableActions = result.availableActions || ["init", "spin"];
        state.flowState = result.flowState || "ready";
        state.features = result.features || null;
        state.freeRounds = result.freeRounds || null;

        if (result.command) {
          state.lastCommand = result.command;
        }

        state.status = "succeeded";
        state.isLoading = false;
      })
      .addCase(initializeGame.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to initialize the game";
        state.isLoading = false;
      })

      // Listen for spin actions from spinSlice
      .addCase(rgsPlay.pending, (state, action) => {
        // Pessimistic update: Don't deduct balance until we get successful response
        // Just set status and clear previous wins

        // Clear all round data but KEEP reels for error recovery
        if (state.currentRound) {
          state.currentRound.win = 0;
          state.currentRound.wins = [];
          state.currentRound.scatterCount = 0;
          state.currentRound.scatterPositions = [];
          state.currentRound.winType = "";
          state.currentRound.isAnimatedWin = false;
          state.currentRound.expandingWildReels = [];
          state.currentRound.hasExpandingWilds = false;
          // DO NOT clear state.currentRound.reels - keep for error recovery!
        }

        state.error = null;
        state.status = "spinning";
      })
      .addCase(rgsPlay.fulfilled, (state, action) => {
        const {
          token,
          bet,
          balance,
          round,
          availableActions,
          flowState,
          features: featuresPayload,
          command,
          freeRounds: freeRoundsData,
          bonusCost,
          gameMode,
          featureState,
        } = action.payload;

        // Simple state updates - RGS has handled all storage operations
        if (token && token !== state.token) {
          state.token = token;
        }

        state.bet = bet !== undefined ? bet : state.bet;

        // BALANCE STRATEGY: Get provider-specific balance management strategy
        const balanceStrategy = handleGetBalanceStrategy();

        // GAME RULE: Determine if bet should be deducted (never during features)
        const isBaseGameSpin = gameMode === "base";

        // Check if free rounds just ended: was "played", now null (localStorage cleared)
        const wasFreeRoundsPlayed = state.freeRounds?.status === "played";
        const isFreeRoundsNowNull =
          freeRoundsData?.status === null || freeRoundsData === null;
        const freeRoundsJustEnded = wasFreeRoundsPlayed && isFreeRoundsNowNull;

        const isFreeRoundsActive =
          state.freeRounds?.status === "active" ||
          (state.freeRounds?.status === "played" && !freeRoundsJustEnded);

        // Apply balance strategy only for base game spins (not during features)
        const shouldDeductBet = isBaseGameSpin && !isFreeRoundsActive;

        if (shouldDeductBet && balanceStrategy.clientDeductsBet) {
          const deductionAmount =
            bonusCost !== undefined
              ? bonusCost
              : bet !== undefined
                ? bet
                : state.bet;
          state.balance = Math.max(0, state.balance - deductionAmount);
          state.displayedBalance = state.balance;
        }

        // Store the round
        if (round) {
          state.currentRound = DEV_SIMULATION_ENABLED ? DEV_SIMULATION_ROUND : round;
        }

        // Store balance from API response
        // Strategy determines how balance is handled:
        // - If serverIncludesWin: balance already has win, sync after animations
        // - If !serverIncludesWin: balance is old, win will be added client-side after animations
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          // displayedBalance stays at deducted amount until WinDisplay updates it
        }

        // Universal state fields (provider-agnostic)
        state.gameMode = gameMode || "base";
        state.featureState = featureState || {
          type: null,
          phase: null,
          data: null,
        };

        // Provider-specific fields (for backward compatibility)
        state.features = featuresPayload || null;
        state.availableActions = availableActions || ["init", "spin"];
        state.flowState = flowState || "ready";
        state.freeRounds = freeRoundsData || null;

        if (command) {
          state.lastCommand = command;
        }

        state.status = "succeeded";
      })
      .addCase(rgsPlay.rejected, (state, action) => {
        const errorData = action.payload;

        /* ============================================================
           DISABLED: Emergency Outcome Handling (Fake Spin Logic)

           Previously, RGS would generate fake non-winning combinations
           for errors. This logic is kept for backward compatibility but
           emergencyOutcome is no longer generated in RGS.js.

           All errors now freeze reels mid-spin via ReelControlContext.js
           instead of landing on fake patterns.
           ============================================================ */

        // Check for emergency outcome (legacy - no longer generated)
        if (errorData?.emergencyOutcome) {
          state.currentRound = {
            reels: errorData.emergencyOutcome.reels,
            wins: errorData.emergencyOutcome.wins || {},
            win: errorData.emergencyOutcome.win || 0,
            bet: errorData.emergencyOutcome.bet || state.bet,
          };
        }
        // No emergency outcome - reels freeze mid-spin instead

        // PESSIMISTIC UPDATE: No need to restore balance since we never deducted it
        // If API provides balance in error response, use it (for consistency)
        if (errorData?.balance !== undefined && errorData.balance !== null) {
          state.balance = Math.max(0, errorData.balance);
          state.displayedBalance = Math.max(0, errorData.balance);
        }
        // Otherwise, balance remains unchanged (as it should be)

        // Parse error response: {"errors":[{"code":301,"desc":"not_enough_money"}]}
        if (
          errorData?.errors &&
          Array.isArray(errorData.errors) &&
          errorData.errors.length > 0
        ) {
          const firstError = errorData.errors[0];
          state.errorCode = firstError.code;
          state.errorDescription = firstError.desc;
          state.error = `Error ${firstError.code}: ${firstError.desc}`;
        } else {
          state.error = errorData?.message || errorData || "Spin failed";
          state.errorCode = null;
          state.errorDescription = null;
        }

        state.status = "failed";
      })

      // Handle buy feature response
      .addCase(rgsBuyFeature.fulfilled, (state, action) => {
        const { balance } = action.payload;
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }
        state.status = "idle";
      })
      .addCase(rgsBuyFeature.rejected, (state, action) => {
        state.error = action.payload || "Buy feature failed";
        state.status = "failed";
      })

      // Handle heartbeat sync - update ONLY balance
      .addCase(heartbeatSync.fulfilled, (state, action) => {
        const { balance } = action.payload;

        // CRITICAL: Update ONLY balance to avoid unnecessary re-renders
        // Do NOT update any other fields even if they exist in the response
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          // Also update displayedBalance so UI reflects the change immediately
          state.displayedBalance = Math.max(0, balance);
        }

        // Reset error state on successful heartbeat
        state.heartbeatError.consecutiveFailures = 0;
        state.heartbeatError.state = "none";
      })
      .addCase(heartbeatSync.rejected, (state, action) => {
        // Increment consecutive failure counter
        state.heartbeatError.consecutiveFailures += 1;

        // Update error state based on failure count
        if (state.heartbeatError.consecutiveFailures >= 10) {
          state.heartbeatError.state = "critical";
        } else if (state.heartbeatError.consecutiveFailures >= 3) {
          state.heartbeatError.state = "warning";
        }

        // Don't set game status to failed - heartbeat errors should not block gameplay
      })

      // Handle syncBalance - lightweight balance-only sync
      .addCase(syncBalance.fulfilled, (state, action) => {
        const { balance } = action.payload;
        if (balance !== undefined) {
          state.balance = Math.max(0, balance);
          state.displayedBalance = Math.max(0, balance);
        }
      })
      .addCase(syncBalance.rejected, () => {
        // Silent fail - balance sync is not critical
      })

      // Handle refreshGameData - lightweight init for error 205 (free rounds)
      // Updates game data WITHOUT resetting spinSlice state or reloading assets
      .addCase(refreshGameData.fulfilled, (state, action) => {
        const { result } = action.payload;

        // Simple state updates - RGS has handled feature detection
        state.balance = Math.max(0, result.balance);
        state.displayedBalance = Math.max(0, result.balance);
        state.bet = result.bet;
        state.bets = result.bets;

        // Update currency config if provided
        if (result.currencyConfig) {
          state.currencyConfig = {
            subunits:
              result.currencyConfig.subunits !== undefined
                ? result.currencyConfig.subunits
                : 100,
            exponent:
              result.currencyConfig.exponent !== undefined
                ? result.currencyConfig.exponent
                : 2,
            symbol: result.currencyConfig.symbol || "",
          };
        }

        // Update current round if provided
        if (result.round) {
          state.currentRound = result.round;
        }

        // CRITICAL: Update featureState/gameMode so first free round spin sends correct request
        state.gameMode = result.gameMode || "base";
        state.featureState = result.featureState || {
          type: null,
          phase: null,
          data: null,
        };
        state.reloadState = result.reloadState || { needsReload: false };

        // Provider-specific fields
        state.availableActions = result.availableActions || ["init", "spin"];
        state.flowState = result.flowState || "ready";
        state.freeRounds = result.freeRounds || null;

        // Clear error state
        state.error = null;
        state.errorCode = null;
        state.errorDescription = null;
        state.status = "idle";
      })
      .addCase(refreshGameData.rejected, (state, action) => {
        state.error = action.payload || "Failed to refresh game data";
        state.status = "failed";
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectToken = (state) => state.game.token;
export const selectBalance = (state) => state.game.balance;
export const selectDisplayedBalance = (state) => state.game.displayedBalance;
export const selectBet = (state) => state.game.bet;
export const selectBaseBet = (state) => state.game.baseBet || 1;
export const selectBets = (state) => state.game.bets;
export const selectCurrency = (state) => state.game.currency;
export const selectCurrencyConfig = (state) => state.game.currencyConfig;
export const selectPaytable = (state) => state.game.paytable;
export const selectRtp = (state) => state.game.rtp;
export const selectBonusBuyMultiplier = (state) =>
  state.game.bonusBuyMultiplier;
export const selectCurrentRound = (state) => state.game.currentRound;
export const selectWinningLines = (state) => state.game.winningLines;
export const selectScatterCount = (state) =>
  state.game.currentRound?.scatterCount || 0;
export const selectScatterPositions = (state) =>
  state.game.currentRound?.scatterPositions || [];
export const selectWinType = (state) => state.game.currentRound?.winType || "";
export const selectGameStatus = (state) => state.game.status;
export const selectGameError = (state) => state.game.error;
export const selectErrorCode = (state) => state.game.errorCode;
export const selectErrorDescription = (state) => state.game.errorDescription;

// Universal state selectors (provider-agnostic)
export const selectGameMode = (state) => state.game.gameMode;
export const selectFeatureState = (state) => state.game.featureState;
export const selectReloadState = (state) => state.game.reloadState;

// Legacy provider-specific selectors (will be removed in Phase 12)
export const selectAvailableActions = (state) => state.game.availableActions;
export const selectFlowState = (state) => state.game.flowState;
export const selectFeatures = (state) => state.game.features;
export const selectLastCommand = (state) => state.game.lastCommand;
export const selectHeartbeatError = (state) => state.game.heartbeatError;
export const selectFreeRounds = (state) => state.game.freeRounds;

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  incrementBet,
  decrementBet,
  setBalance,
  setBet,
  setBaseBet,
  setDisplayedBalance,
  updateDisplayedBalance,
  setAvailableActions,
  setFlowState,
  setLastCommand,
  restoreSavedBet,
  clearFeatureStateToBase,
  setFeatureStateFromFreeRounds,
  updateReelSymbols,
} = gameSettingsSlice.actions;

// Export the reducer to integrate into the Redux store
export default gameSettingsSlice.reducer;
